import type { RulePlanExistingOutput } from "@kwp/contracts";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, useTRPC } from "../../../lib/api";
import { Button, ErrorText } from "../../../ui";
import { Dialog } from "../../../ui/overlay";
import { useToast } from "../../../ui/toast";

/**
 * "Apply to existing mail" (§7): the bulk plans for what the rule's query matches now, one per
 * action, each applied exactly as planned (spam and trash in bulk wait for approval, §9).
 */
export function ApplyExistingDialog({ ruleName, plans, onClose }: { ruleName: string; plans: RulePlanExistingOutput; onClose: () => void }) {
  const trpc = useTRPC();
  const toast = useToast();
  const [applied, setApplied] = useState<string[]>([]);
  const apply = useMutation(
    trpc.actions.applyPlan.mutationOptions({
      onSuccess: (out, input) => {
        setApplied((a) => [...a, input.planId]);
        toast({ message: out.status === "awaiting_approval" ? "Waiting for your approval in Approvals." : "Applied." });
      },
    }),
  );
  const nothing = plans.plans.every((p) => p.planId === null);
  return (
    <Dialog title={`Apply “${ruleName}” to existing mail`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {nothing ? <p className="text-sm text-muted">No existing mail matches this rule.</p> : null}
        {plans.plans
          .filter((p) => p.planId !== null)
          .map((p) => (
            <div key={p.planId} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
              <div className="min-w-0">
                <p className="text-sm text-text">{p.summary}</p>
                {p.capability === "destructive" ? <p className="text-xs text-muted">Needs your approval.</p> : null}
              </div>
              <Button
                variant="primary"
                disabled={apply.isPending || applied.includes(p.planId as string)}
                onClick={() => apply.mutate({ planId: p.planId as string })}
              >
                {applied.includes(p.planId as string) ? "Done" : `Apply to ${p.matched}`}
              </Button>
            </div>
          ))}
        {plans.skipped.map((s) => (
          <p key={s} className="text-xs text-muted">
            Not applied: {s}
          </p>
        ))}
        {apply.isError ? <ErrorText>{errorMessage(apply.error)}</ErrorText> : null}
      </div>
    </Dialog>
  );
}
